import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { apiHelpers } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useChartBuilderStore } from '../../store/chartBuilderStore';
import { useCreditCosts } from '../../hooks/useCreditCosts';
import ThinkingChain from '../common/ThinkingChain';
import {
  SparklesIcon,
  PaperAirplaneIcon,
  UserIcon,
} from '@heroicons/react/24/outline';

const SUGGESTIONS = [
  'Show sales by month',
  'Top 10 products by revenue',
  'Customer growth over time',
  'Distribution of order values',
];

export default function AIAssistantTab() {
  const { user, getCurrentCompanyId } = useAuthStore();
  const { formatCost } = useCreditCosts();
  const messagesEndRef = useRef(null);

  const {
    chartType,
    chartConfig,
    chartName,
    sourceMode,
    selectedDataSource,
    selectedDataSources,
    setPreviewError,
  } = useChartBuilderStore();

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [thinkingStep, setThinkingStep] = useState(0);
  const [thinkingStartTime, setThinkingStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [stepDetails, setStepDetails] = useState({});
  const [provider] = useState(user?.preferences?.defaultAIProvider || 'anthropic');
  const [model, setModel] = useState('');

  const { data: modelsData } = useQuery({
    queryKey: ['aiModels'],
    queryFn: () => apiHelpers.getModels(),
    staleTime: 5 * 60 * 1000,
  });


  useEffect(() => {
    if (modelsData?.data?.providers?.[provider]) {
      setModel(modelsData.data.providers[provider].defaultModel);
    }
  }, [provider, modelsData]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!thinkingStartTime) return;
    const interval = setInterval(() => {
      setElapsedTime(Date.now() - thinkingStartTime);
    }, 100);
    return () => clearInterval(interval);
  }, [thinkingStartTime]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);
    setPreviewError(null);
    setThinkingStep(0);
    setThinkingStartTime(Date.now());
    setElapsedTime(0);
    setStepDetails({});

    try {
      const companyId = getCurrentCompanyId();
      const conversationHistory = messages.map((m) => ({
        role: m.role,
        content: m.content,
      }));

      const payload = {
        message: userMessage,
        companyId,
        dataSourceId:
          sourceMode === 'auto'
            ? 'auto'
            : sourceMode === 'multi'
              ? selectedDataSources[0]
              : selectedDataSource,
        dataSourceIds: sourceMode === 'multi' ? selectedDataSources : [],
        currentChartType: chartType,
        existingConfig: chartConfig,
        existingQuery: chartConfig?.query,
        existingChartName: chartName || chartConfig?.name,
        isEditMode: !!chartConfig,
        conversationHistory,
        provider,
        model,
      };

      const response = await apiHelpers.generateChartWithAIStream(payload, (progressData) => {
        const step = progressData.step != null ? progressData.step : 0;
        setThinkingStep(step);
        if (progressData.details != null) {
          setStepDetails((prev) => ({ ...prev, [step]: progressData.details }));
        }
      });

      const responseData = response.data;

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: responseData.response,
          chartSuggestion: responseData.chartSuggestion,
        },
      ]);

      useChartBuilderStore.getState().setFromAIResponse(responseData);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content:
            'Sorry, I encountered an error generating the chart. Please try again or rephrase your request.',
          isError: true,
        },
      ]);
      setPreviewError(error.response?.data?.error || error.message || 'Failed to generate chart');
    } finally {
      setThinkingStep(0);
      setThinkingStartTime(null);
      setElapsedTime(0);
      setStepDetails({});
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center mb-4">
              <SparklesIcon className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-lg font-display font-bold text-surface-900 dark:text-white mb-2">
              What would you like to visualize?
            </h3>
            <p className="text-surface-500 max-w-sm mb-6">
              Tell me what data you want to see, and I'll create the perfect chart for you.
            </p>
            <div className="flex flex-wrap gap-2 justify-center max-w-md">
              {SUGGESTIONS.map((suggestion, i) => (
                <button
                  key={i}
                  onClick={() => setInput(suggestion)}
                  className="px-3 py-2 rounded-full bg-surface-100 dark:bg-surface-800 text-sm text-surface-600 dark:text-surface-400 hover:bg-primary-50 hover:text-primary-600 dark:hover:bg-primary-900/30 dark:hover:text-primary-400 transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    msg.role === 'assistant'
                      ? 'bg-gradient-to-br from-primary-500 to-accent-500'
                      : 'bg-surface-200 dark:bg-surface-700'
                  }`}
                >
                  {msg.role === 'assistant' ? (
                    <SparklesIcon className="w-4 h-4 text-white" />
                  ) : (
                    <UserIcon className="w-4 h-4 text-surface-600 dark:text-surface-400" />
                  )}
                </div>
                <div className={`max-w-[85%] ${msg.role === 'user' ? 'text-right' : ''}`}>
                  <div
                    className={`inline-block px-4 py-3 rounded-2xl ${
                      msg.role === 'assistant'
                        ? msg.isError
                          ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                          : 'bg-surface-100 dark:bg-surface-800 text-surface-900 dark:text-white'
                        : 'bg-primary-600 text-white'
                    }`}
                  >
                    {msg.role === 'assistant' ? (
                      <div className="prose prose-sm dark:prose-invert max-w-none">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                      </div>
                    ) : (
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {isLoading && (
              <ThinkingChain
                currentStep={Math.min(thinkingStep, 4)}
                elapsedTime={elapsedTime}
                stepDetails={stepDetails}
              />
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="p-3 sm:p-4 border-t border-surface-200 dark:border-surface-800 bg-surface-50 dark:bg-surface-800/50">
        <div className="flex gap-2 sm:gap-3">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe what you want to visualize..."
            rows={2}
            className="flex-1 input resize-none text-base"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            className="btn-primary self-end px-3 sm:px-4"
            title={formatCost('chart_builder') ? `Send message ${formatCost('chart_builder')}` : ''}
          >
            <PaperAirplaneIcon className="w-5 h-5" />
            {formatCost('chart_builder') && (
              <span className="ml-1 text-xs opacity-75 hidden sm:inline">
                {formatCost('chart_builder')}
              </span>
            )}
          </button>
        </div>
        <p className="text-xs text-surface-500 mt-2 hidden sm:block">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
  );
}
